import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/index.ts";
import { setQuery, setFetch } from "../../store/slices/directBossSlice.ts";
import { fetchPartners, readPartnersByBranch } from "../../services/partner.service.ts";
import { Partner } from "../../models/Partner.ts";
import ModalContainer from "../ModalContainer.tsx";
import DirectBossForm from "./DirectBossForm.tsx";
import LookupCollaboratorBossModal from "./LookupCollaboratorBossModal.tsx";
import { DirectBoss } from "../../services/direct-boss.service.ts";

const FilterDirectBoss = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { query } = useSelector((state: RootState) => state.directBoss);
  
  const [partners, setPartners] = useState<Partner[]>([]);
  const [filters, setFilters] = useState({
    search: "",
    bossId: -1,
    employeeId: -1,
    isActive: "activos",
  });
  const filtersRef = useRef(filters);
  const [timer, setTimer] = useState<any>(null);
  const [showPills, setShowPills] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLookupModal, setShowLookupModal] = useState(false);
  const [editingDirectBoss, setEditingDirectBoss] = useState<DirectBoss | null>(null);

  const fetchPartnersData = async () => {
    try {
      const response = await fetchPartners("?limit=1000");
      setPartners(response.results);
    } catch (error) {
      console.error("Error fetching partners:", error);
    }
  };

  useEffect(() => {
    fetchPartnersData();
  }, []);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const trimmedValue = rawValue.trim();
    setFilters((prevFilters) => ({ ...prevFilters, search: rawValue }));
    setShowPills(trimmedValue !== "");

    if (timer) clearTimeout(timer);

    setTimer(
      setTimeout(() => {
        const updatedFilters = {
          ...filtersRef.current,
          search: trimmedValue,
        };

        const queryString = Object.keys(updatedFilters)
          .filter(
            (key) => updatedFilters[key] !== -1 && updatedFilters[key] !== ""
          )
          .map(
            (key) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(
                updatedFilters[key]
              )}`
          )
          .join("&");

        dispatch(setQuery(queryString));
        setTimer(null);
      }, 500)
    );
  };

  const updateFilters = (params?: {}) => {
    const filtros = { ...filters, ...params };
    const queryString = Object.keys(filtros)
      .filter((key) => filtros[key] !== -1 && filtros[key] !== "")
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(filtros[key])}`
      )
      .join("&");
    dispatch(setQuery(queryString));
  };

  const selectedFilters = (filters: any) => {
    let selected: any = [];
    for (const key in filters) {
      if (
        filters[key] !== -1 &&
        filters[key] !== "" &&
        filters[key] !== "activos"
      ) {
        let value = filters[key];
        let valueName = "";
        let keyName = "";
        
        switch (key) {
          case "search":
            keyName = "Búsqueda";
            valueName = value;
            break;
          case "bossId":
            keyName = "Jefe";
            const boss = partners.find((item) => item.id === value);
            valueName = boss ? `${boss.person.firstName} ${boss.person.lastName}` : "";
            break;
          case "employeeId":
            keyName = "Empleado";
            const employee = partners.find((item) => item.id === value);
            valueName = employee ? `${employee.person.firstName} ${employee.person.lastName}` : "";
            break;
          case "isActive":
            keyName = "Estado";
            valueName = value === "activos" ? "Activos" : "Inactivos";
            break;
          default:
            keyName = key;
            valueName = value;
            break;
        }
        
        if (valueName) {
          selected.push({ key, value, valueName, keyName });
        }
      }
    }

    if (selected.length === 0) setShowPills(false);
    return selected;
  };

  const removeFilter = (key: string) => {
    const initialValues = {
      search: "",
      bossId: -1,
      employeeId: -1,
      isActive: "activos",
    };
    
    setFilters({ ...filters, [key]: initialValues[key] });

    const updatedFilters = { ...filters, [key]: initialValues[key] };
    const queryString = Object.keys(updatedFilters)
      .filter(
        (key) =>
          updatedFilters[key] !== -1 &&
          updatedFilters[key] !== "" &&
          updatedFilters[key] !== "activos"
      )
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(
            updatedFilters[key]
          )}`
      )
      .join("&");
    dispatch(setQuery(queryString));
  };

  const handleOpenModal = () => {
    setEditingDirectBoss(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDirectBoss(null);
  };

  const handleSuccess = () => {
    dispatch(setFetch(true)); // Trigger refresh
    handleCloseModal();
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        className="input-group mb-2"
        style={{
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <button
          className="btn btn-white dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
        >
          <span className="d-none d-md-inline">Filtrar</span>
          <span className="d-inline d-md-none">
            <i className="fa fa-filter"></i>
          </span>
          <b className="caret"></b>
        </button>
        <div className="dropdown-menu p-4" onClick={(e) => e.stopPropagation()}>
          <label className="mt-2">Estado:</label>
          <select
            className="form-select mt-1"
            id="isActive"
            name="isActive"
            onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
            value={filters.isActive}
          >
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
            <option value="todos">Todos</option>
          </select>

          <label className="mt-2">Jefe:</label>
          <select
            className="form-select mt-1"
            id="bossId"
            name="bossId"
            onChange={(e) =>
              setFilters({ ...filters, bossId: Number(e.target.value) })
            }
            value={filters.bossId}
          >
            <option value={-1} disabled>
              Seleccionar jefe
            </option>
            {partners.map((item) => (
              <option key={item.id} value={item.id}>
                {item.person.firstName} {item.person.lastName}
              </option>
            ))}
          </select>

          <label className="mt-2">Empleado:</label>
          <select
            className="form-select mt-1"
            id="employeeId"
            name="employeeId"
            onChange={(e) =>
              setFilters({ ...filters, employeeId: Number(e.target.value) })
            }
            value={filters.employeeId}
          >
            <option value={-1} disabled>
              Seleccionar empleado
            </option>
            {partners.map((item) => (
              <option key={item.id} value={item.id}>
                {item.person.firstName} {item.person.lastName}
              </option>
            ))}
          </select>

          <div className="row mt-3">
            <div className="col">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const initialValues = {
                    search: "",
                    bossId: -1,
                    employeeId: -1,
                    isActive: "activos",
                  };
                  setFilters(initialValues);
                  updateFilters(initialValues);
                  setShowPills(false);
                }}
                className="btn btn-amarillo d-block w-100 btn-lg fs-14px"
                type="button"
              >
                Limpiar
              </button>
            </div>
            <div className="col">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  updateFilters();
                  setShowPills(true);
                }}
                className="btn btn-azul d-block w-100 btn-lg fs-14px"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>

        <div className="flex-fill position-relative">
          <div className="input-group">
            <div
              className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
              style={{ zIndex: 10 }}
            >
              {timer ? (
                <i className="fa fa-spinner fa-spin"></i>
              ) : (
                <i className="fa fa-search opacity-5"></i>
              )}
            </div>
            <input
              type="text"
              className="form-control px-35px bg-light"
              placeholder="Buscar por nombre..."
              id="search"
              name="search"
              onChange={handleSearchChange}
              value={filters.search}
            />
          </div>
        </div>
        <button
          type="button"
          className="btn btn-white"
          style={{ marginLeft: 4, border: "1px solid #e2e8f0", whiteSpace: "nowrap" }}
          onClick={() => setShowLookupModal(true)}
          title="Encontrar jefe directo de un colaborador"
        >
          <i className="fa fa-user-tie me-2" />
          <span className="d-none d-lg-inline">
            Encontrar jefe directo de colaborador
          </span>
          <span className="d-inline d-lg-none">Encontrar jefe directo</span>
        </button>
        {Boolean(user?.rol === "Administrador" || user?.rol === "Lider de sucursal") && (
          <button
            className="btn btn-azul"
            style={{ marginLeft: 10 }}
            onClick={handleOpenModal}
          >
            <i className="fa fa-plus me-2"></i>
            Nuevo Jefe Directo
          </button>
        )}
      </div>
      
      <div className="mb-3">
        {showPills &&
          selectedFilters(filters).map((filter: any) => (
            <div key={filter.key} className="badge bg-primary text-white fs-6 me-2 position-relative pe-4">
              {filter.keyName}: {filter.valueName}
              <button
                type="button"
                className="btn-close btn-close-white position-absolute top-0 end-0 mt-1 me-1 ps-1"
                aria-label="Close"
                onClick={() => removeFilter(filter.key)}
              ></button>
            </div>
          ))}
      </div>

      <LookupCollaboratorBossModal
        open={showLookupModal}
        onClose={() => setShowLookupModal(false)}
      />

      <ModalContainer
        open={showModal}
        closeFn={handleCloseModal}
        title={editingDirectBoss ? "Editar Jefe Directo" : "Nuevo Jefe Directo"}
        size="lg"
      >
        <DirectBossForm
          close={handleCloseModal}
          onSuccess={handleSuccess}
          directBossToEdit={editingDirectBoss}
          isEditing={!!editingDirectBoss}
        />
      </ModalContainer>
    </div>
  );
};

export default FilterDirectBoss;
